import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EnhancedOrderDetailView from './EnhancedOrderDetailView';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 2: AI Products Orders Management
 * Phase 3: Enhanced with comprehensive details
 * Phase 4: Advanced search, filters, sorting, bulk download
 * Admin interface for viewing AI product orders
 */
const AIProductsOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  
  // Phase 4: Advanced filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const limit = 20;
  
  // Bulk selection and delete
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Load orders
  useEffect(() => {
    loadOrders();
  }, [searchQuery, statusFilter, customerFilter, dateFrom, dateTo, sortBy, sortOrder, currentPage]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (currentPage * limit).toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (customerFilter) params.append('customer_email', customerFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      // Get authentication token from localStorage
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      console.log('[AIProductsOrders] Loading orders from:', `${API}/orders/?${params.toString()}`);
      console.log('[AIProductsOrders] Auth token present:', !!token);
      
      const response = await axios.get(`${API}/api/orders/?${params.toString()}`, { headers });
      
      console.log('[AIProductsOrders] Response received:', {
        total: response.data.total,
        count: response.data.orders?.length,
        orderIds: response.data.orders?.map(o => o.order_id)
      });
      
      setOrders(response.data.orders || []);
      setTotalOrders(response.data.total || 0);
      setLoading(false);
    } catch (err) {
      console.error('[AIProductsOrders] Error loading orders:', err);
      console.error('[AIProductsOrders] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Failed to load orders. Please try again.');
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/api/orders/${orderId}`, { headers });
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
    } catch (err) {
      console.error('Error loading order details:', err);
      alert('Failed to load order details');
    }
  };

  const handleBulkDownload = async (orderId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/api/orders/${orderId}/files/bulk`, { headers });
      const files = response.data.files;
      
      if (files.length === 0) {
        alert('No files available for download');
        return;
      }
      
      // Download each file
      files.forEach(file => {
        window.open(file.url, '_blank');
      });
      
      alert(`Downloading ${files.length} file(s) from order ${orderId.substring(0, 8)}...`);
    } catch (err) {
      console.error('Error bulk downloading:', err);
      alert('Failed to download files');
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
      setSelectAll(false);
    } else {
      setSelectedOrders(orders.map(order => order.order_id));
      setSelectAll(true);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete order ${orderId.substring(0, 8)}?\n\nThis will permanently delete:\n- Order record\n- STL files\n- Uploaded images\n- Preview images\n- Job records\n- Artifacts\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${API}/api/orders/${orderId}`, { headers });
      alert('Order deleted successfully');
      loadOrders(); // Reload the list
    } catch (err) {
      console.error('Error deleting order:', err);
      alert(`Failed to delete order: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select orders to delete');
      return;
    }
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedOrders.length} order(s)?\n\nThis will permanently delete:\n- Order records\n- STL files\n- Uploaded images\n- Preview images\n- Job records\n- Artifacts\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/api/orders/bulk-delete`, selectedOrders, { headers });
      
      alert(`Successfully deleted ${response.data.total_deleted} order(s)\nFailed: ${response.data.total_failed}`);
      
      setSelectedOrders([]);
      setSelectAll(false);
      loadOrders(); // Reload the list
    } catch (err) {
      console.error('Error bulk deleting orders:', err);
      alert(`Failed to delete orders: ${err.response?.data?.detail || err.message}`);
    }
  };


  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCustomerFilter('');
    setDateFrom('');
    setDateTo('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'stl_generated': 'bg-green-100 text-green-800',
      'completed': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const activeFiltersCount = [searchQuery, statusFilter, customerFilter, dateFrom, dateTo].filter(Boolean).length;

  // Order List View
  if (!showOrderDetail) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Products Orders</h1>
          <p className="text-gray-600">View and manage all AI product orders with advanced search and filters</p>
        </div>

        {/* Phase 4: Advanced Search & Filters */}
        <div className="bg-white rounded-lg shadow mb-4 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🔍 Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Order ID, Email, Job ID..."
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="stl_generated">STL Generated</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Customer Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Email
              </label>
              <input
                type="text"
                value={customerFilter}
                onChange={(e) => {
                  setCustomerFilter(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="customer@example.com"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="created_at">Created Date</option>
                <option value="updated_at">Updated Date</option>
                <option value="status">Status</option>
                <option value="user_email">Customer Email</option>
                <option value="stl_generated_at">STL Generated Date</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <>
                  <span className="text-sm text-gray-600">
                    {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
                  </span>
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear All
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {selectedOrders.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  🗑️ Delete Selected ({selectedOrders.length})
                </button>
              )}
              <button
                onClick={loadOrders}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        ) : (
          <>
            {/* Orders Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          No orders found {activeFiltersCount > 0 && 'matching your filters'}
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.order_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.order_id)}
                              onChange={() => handleSelectOrder(order.order_id)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-mono text-gray-900" title={order.order_id}>
                              {order.order_id.substring(0, 8)}...
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{order.user_email}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {order.product_details?.product_type} ({order.product_details?.size})
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-500">
                              {formatDate(order.created_at)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleViewOrder(order.order_id)}
                                className="text-blue-600 hover:text-blue-900 font-medium"
                                title="View Details"
                              >
                                👁️ View
                              </button>
                              <button
                                onClick={() => handleBulkDownload(order.order_id)}
                                className="text-green-600 hover:text-green-900 font-medium"
                                title="Bulk Download All Files"
                              >
                                📥 Download
                              </button>
                              <button
                                onClick={() => handleDeleteOrder(order.order_id)}
                                className="text-red-600 hover:text-red-900 font-medium"
                                title="Delete Order"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalOrders > limit && (
              <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{currentPage * limit + 1}</span> to{' '}
                  <span className="font-medium">{Math.min((currentPage + 1) * limit, totalOrders)}</span> of{' '}
                  <span className="font-medium">{totalOrders}</span> orders
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Previous
                  </button>
                  <div className="px-4 py-2 text-sm text-gray-700">
                    Page {currentPage + 1} of {Math.ceil(totalOrders / limit)}
                  </div>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={(currentPage + 1) * limit >= totalOrders}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Order Detail View
  return (
    <EnhancedOrderDetailView 
      order={selectedOrder} 
      onBack={() => {
        setShowOrderDetail(false);
        setSelectedOrder(null);
      }}
      formatDate={formatDate}
      getStatusBadge={getStatusBadge}
      onBulkDownload={handleBulkDownload}
    />
  );
};

export default AIProductsOrders;
